/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Action, State } from '@table-library/react-table-library/common';
import {
  Table,
  Data,
  Header,
  HeaderRow,
  HeaderCell,
  Body,
  Row,
  Cell,
  TableNode,
} from '@table-library/react-table-library/table';
import { CellTree, useTree } from '@table-library/react-table-library/tree';
import { CellSelect, SelectClickTypes, SelectTypes, useRowSelect } from '@table-library/react-table-library/select';
import { DATE, formatDate, getMonthName, MONTHS_VALUE } from '../../../utils/date';
import { SurveySummary, SurveyType } from '../../../types/survey';
import { UncontrolledDropdown, DropdownItem, DropdownMenu, DropdownToggle, Button } from 'reactstrap';
import { BasicAlert } from '../../alert/BasicAlert';
import { useAppSelector } from '../../../reducers';
import { useDispatch } from 'react-redux';
import { CustomReportType, DateReportType } from '../../../types/custom-report';
import dayjs from 'dayjs';
import { loadSurveyListSummaryByInitiativeId } from '../../../actions/survey';
import { useTheme } from '@table-library/react-table-library/theme';

export interface DateSurveySelectionTableProps {
  initiativeId: string;
  defaultSelected?: string[];
  defaultType?: DateReportType;
  selectedSurveyIds: string[];
  handleSelectSurveyType: (type: DateReportType) => void;
  handleSelectSurveys: (ids: string[]) => void;
}

interface TableNodeWithSurveyData extends TableNode {
  _id: string;
  name: string;
  surveyName: string;
  type: SurveyType;
  effectiveDate: string;
  status: {
    created: number;
    updated: number;
    rejected: number;
    verified: number;
  };
}

const SURVEY_TYPE = {
  [SurveyType.Default]: 'Surveys',
  [SurveyType.Aggregation]: 'Combined Survey',
  [SurveyType.Materiality]: 'Materiality Survey',
  [SurveyType.AutoAggregation]: 'Auto-aggregated Survey',
};

const CUSTOM_REPORT_TYPE_MAPPING = {
  [CustomReportType.Survey]: 'Surveys',
  [CustomReportType.SurveyAggregation]: 'Combined Survey',
};

interface DateCustomReportData {
  _id: string;
  effectiveDate: string;
  type: SurveyType;
  surveyName: string;
  status: {
    created: number;
    updated: number;
    rejected: number;
    verified: number;
  };
}

interface Filters {
  surveyType?: DateReportType;
  year?: number;
  month?: number;
}

// Sort asc survey effectiveDate
const sortByDate = (nodes: TableNodeWithSurveyData[]) =>
  nodes.sort((a, b) => (dayjs.utc(a.effectiveDate).isBefore(dayjs.utc(b.effectiveDate)) ? -1 : 1));

const buildTree = (list: SurveySummary[], filters: Filters): { data: Data; numOfNodes: number } => {
  const yearMap = new Map<string, Map<string, Map<string, DateCustomReportData>>>();

  list.forEach((survey) => {
    const { type = SurveyType.Default } = survey;
    const year = formatDate(survey.effectiveDate, DATE.YEAR_ONLY, true);
    const surveyName = `${formatDate(survey.effectiveDate, DATE.MONTH_YEAR, true)}${survey.name ? ` - ${survey.name}` : ''}`;
    const formatedSurvey = {
      _id: survey._id,
      effectiveDate: survey.effectiveDate,
      surveyName,
      type,
      status: survey.status,
    };
    if (!yearMap.has(year)) {
      yearMap.set(year, new Map());
    }
    const surveyTypeMap = yearMap.get(year);
    if (!surveyTypeMap) return;

    if (!surveyTypeMap.has(SURVEY_TYPE[type])) {
      surveyTypeMap.set(SURVEY_TYPE[type], new Map());
    }

    const surveyMap = surveyTypeMap.get(SURVEY_TYPE[type]);
    if (!surveyMap) return;

    if (!surveyMap.has(surveyName)) {
      surveyMap.set(surveyName, formatedSurvey);
    }
  });

  const data: Data = {
    nodes: [],
  };
  let numOfNodes = 0;

  yearMap.forEach((surveyTypeMap, year) => {
    if (filters.year && filters.year !== Number(year)) {
      return;
    }

    const yearNodes: TableNode[] = [];
    surveyTypeMap.forEach((surveyMap, surveyType) => {
      if (filters.surveyType && CUSTOM_REPORT_TYPE_MAPPING[filters.surveyType] !== surveyType) {
        return;
      }

      const surveyNodes: TableNodeWithSurveyData[] = [];
      surveyMap.forEach((surveyItem, surveyId) => {
        const date = surveyItem.effectiveDate;
        if (filters.month && filters.month !== Number(formatDate(date, 'M', true))) {
          return;
        }

        surveyNodes.push({
          id: `${year}-${surveyType}-${surveyId}`,
          name: surveyItem.surveyName,
          ...surveyItem,
        });
      });

      numOfNodes += surveyNodes.length;
      yearNodes.push({
        id: `${year}-${surveyType}`,
        name: surveyType,
        nodes: sortByDate(surveyNodes),
      });
    });

    data.nodes.push({
      id: year,
      name: year,
      nodes: yearNodes,
    });
  });

  // sort by year
  data.nodes.sort((a, b) => a.name - b.name);

  return { data, numOfNodes };
};

const findNodesByIds = (tableData: Data, ids?: string[], isSurveyId?: boolean): TableNodeWithSurveyData[] => {
  if (!ids || ids.length === 0) {
    return [];
  }

  let nodesMatch: TableNodeWithSurveyData[] = [];
  tableData.nodes.forEach((year) =>
    year.nodes?.some((surveyType) => {
      const nodesFound = surveyType.nodes?.filter((survey) => {
        if (isSurveyId) {
          return ids.includes(survey._id);
        }
        return ids.includes(survey.id);
      }) as TableNodeWithSurveyData[];

      if (nodesFound && nodesFound.length > 0) {
        nodesMatch = [...nodesMatch, ...nodesFound];
        return true;
      }
      return false;
    })
  );
  return nodesMatch;
};

const getSurveyIds = (nodes: TableNodeWithSurveyData[]) => nodes.map((node) => node._id);
const getNodeIds = (surveys: SurveySummary[], ids: string[]) => {
  const matchSurveys = surveys.filter((item) => ids.includes(item._id));
  return matchSurveys.map((survey) => {
    const { type = SurveyType.Default } = survey;
    const year = formatDate(survey.effectiveDate, DATE.YEAR_ONLY, true);
    const surveyType = SURVEY_TYPE[type];
    return `${year}-${surveyType}-${survey._id}`;
  });
};

const getInitialFilters = (defaultType: DateReportType): Filters => {
  return {
    surveyType: defaultType,
    month: undefined,
    year: undefined,
  };
};

export const DateSurveySelectionTable = (props: DateSurveySelectionTableProps) => {
  const {
    initiativeId,
    defaultSelected = [],
    defaultType = CustomReportType.Survey,
    selectedSurveyIds = [],
    handleSelectSurveyType,
    handleSelectSurveys,
  } = props;
  const dispatch = useDispatch();
  const [selectedNodes, setSelectedNodes] = useState<TableNodeWithSurveyData[]>([]);
  const [filters, setFilters] = useState<Filters>(getInitialFilters(defaultType));
  const surveyListSummaryState = useAppSelector((s) => s.surveyListSummary);

  const surveyList: SurveySummary[] = useMemo(() => {
    return surveyListSummaryState.data || [];
  }, [surveyListSummaryState.data]);

  const { data: tableData, numOfNodes } = useMemo(() => buildTree(surveyList, filters), [surveyList, filters]);

  useEffect(() => {
    if (initiativeId) {
      dispatch(loadSurveyListSummaryByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId]);

  useEffect(() => {
    // preset selected notes
    if (defaultSelected && defaultSelected.length > 0) {
      setSelectedNodes(findNodesByIds(tableData, defaultSelected, true));
    }
  }, [defaultSelected, tableData]);

  const years = useMemo(
    () =>
      Array.from(new Set(surveyList.map((n) => Number(formatDate(n.effectiveDate, DATE.YEAR_ONLY, true)))).values()).sort(),
    [surveyList]
  );

  // This ids is for the purpose of opening the table tree
  const openTreeIds = useMemo(() => {
    let openIds: string[] = [];

    if (defaultSelected && defaultSelected.length > 0) {
      const nodeIds = getNodeIds(surveyList, defaultSelected);
      nodeIds.forEach((id) => {
        const parts = (id ?? '').split('-');
        // Must be 3 parts, `${year}-${surveyType}-${surveyId}`
        openIds = parts.length >= 3 ? [...openIds, parts[0], `${parts[0]}-${parts[1]}`] : openIds;
      });
    }

    return openIds;
  }, [defaultSelected, surveyList]);

  const theme = useTheme({
    Table: `
      height: unset;
      max-height: 100%;
      --data-table-library_grid-template-columns: 70% 7% 7% minmax(0, 1fr);
    `,
  });

  const tree = useTree(
    tableData,
    { state: { ids: openTreeIds } },
    {
      treeIcon: {
        margin: '4px',
        iconDefault: <i className='fa mx-2' />,
        iconRight: <i className='fa fa-caret-right text-ThemeAccentExtradark mx-2' />,
        iconDown: <i className='fa fa-caret-down text-ThemeAccentExtradark mx-2' />,
      },
    }
  );

  const select = useRowSelect(
    tableData,
    {
      state: { ids: selectedNodes.map((item) => item.id) },
      onChange: onSelectChange,
    },
    {
      clickType: SelectClickTypes.RowClick,
      rowSelect: filters.surveyType === CustomReportType.Survey ? SelectTypes.MultiSelect : SelectTypes.SingleSelect,
      buttonSelect: filters.surveyType === CustomReportType.Survey ? SelectTypes.MultiSelect : SelectTypes.SingleSelect,
    }
  );

  const toggleFilter = (key: keyof Filters, value?: DateReportType | number) => {
    if (key === 'surveyType' && value !== filters.surveyType) {
      setSelectedNodes([]);
      handleSelectSurveys([]);
      handleSelectSurveyType((value as DateReportType) ?? CustomReportType.Survey);
    }
    setFilters((state) => ({
      ...state,
      [key]: state[key] === value ? undefined : value,
    }));
  };

  function onSelectChange(action: Action, state: State) {
    const ids = filters.surveyType === CustomReportType.Survey ? state.ids : [state.id];
    const nodes = findNodesByIds(tableData, ids);
    handleSelectSurveys(getSurveyIds(nodes));
  }

  return (
    <div className='collapsible-table'>
      <div className='d-flex align-items-center'>
        <div className='mr-3 strong'>Filters:</div>
        <div className='mr-3 date-filter--dropdown'>
          <UncontrolledDropdown>
            <DropdownToggle className='date-filter--dropdown-toggle' color='secondary' size='sm' caret outline>
              {filters.surveyType === CustomReportType.SurveyAggregation ? 'Combined Surveys' : 'Surveys'}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={() => toggleFilter('surveyType', CustomReportType.Survey)}>Surveys</DropdownItem>
              <DropdownItem onClick={() => toggleFilter('surveyType', CustomReportType.SurveyAggregation)}>
                Combined Surveys
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <div className='mr-3 date-filter--dropdown'>
          <UncontrolledDropdown>
            <DropdownToggle className='date-filter--dropdown-toggle' color='secondary' size='sm' caret outline>
              {filters.year ?? 'Year'}
            </DropdownToggle>
            <DropdownMenu>
              {filters.year ? (
                <DropdownItem onClick={() => toggleFilter('year', undefined)}>
                  <i className='fa fa-times text-ThemeDangerMedium mr-2' />
                  Remove Filter
                </DropdownItem>
              ) : null}
              {years.map((year) => (
                <DropdownItem key={`ddy-${year}`} onClick={() => toggleFilter('year', year)}>
                  {year}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <div className='mr-3 date-filter--dropdown'>
          <UncontrolledDropdown>
            <DropdownToggle className='date-filter--dropdown-toggle' color='secondary' size='sm' caret outline>
              {filters.month ? getMonthName(filters.month) : 'Month'}
            </DropdownToggle>
            <DropdownMenu>
              {filters.month ? (
                <DropdownItem onClick={() => toggleFilter('month', undefined)}>
                  <i className='fa fa-times text-ThemeDangerMedium mr-2' />
                  Remove Filter
                </DropdownItem>
              ) : null}
              {MONTHS_VALUE.map((month) => (
                <DropdownItem key={`ddm-${month}`} onClick={() => toggleFilter('month', month)}>
                  {getMonthName(month)}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
        <div className='ml-auto'>{`${selectedSurveyIds?.length ?? 0} surveys selected`}</div>
      </div>
      <div
        className='mt-2'
        style={{
          height: '400px',
        }}
      >
        {tableData.nodes.length > 0 ? (
          <Table data={tableData} tree={tree} select={select} theme={theme} layout={{ fixedHeader: true, custom: true }}>
            {(tableList) => (
              <>
                <Header>
                  <HeaderRow>
                    <HeaderCell>Survey dates</HeaderCell>
                    <HeaderCell>
                      <i className='fa fa-user-clock text-primary' />
                    </HeaderCell>
                    <HeaderCell>
                      <i className='fa fa-user-check text-success' />
                    </HeaderCell>
                    <HeaderCell>
                      <Button color='link' size='sm' onClick={() => select.fns.onToggleAll({ isPartialToAll: true })}>
                        {selectedSurveyIds.length >= numOfNodes ? 'Unselect all' : 'Select all'}
                      </Button>
                    </HeaderCell>
                  </HeaderRow>
                </Header>

                <Body>
                  {tableList.map((item) => (
                    <Row key={item.id} item={item}>
                      <CellTree item={item}>{item.name}</CellTree>
                      <Cell>{item?.status?.created ?? 0}</Cell>
                      <Cell>{item?.status?.verified ?? 0}</Cell>
                      {Array.isArray(item.nodes) ? <Cell /> : <CellSelect item={item} />}
                    </Row>
                  ))}
                </Body>
              </>
            )}
          </Table>
        ) : (
          <BasicAlert className='mt-3' type='warning'>
            No surveys are available
          </BasicAlert>
        )}
      </div>
    </div>
  );
};
